import { addDays, addMonths, differenceInCalendarDays, format, isValid } from "date-fns";
import { nl } from "date-fns/locale";

// event_date wordt behandeld als een abstracte kalenderdatum (YYYY-MM-DD)
// zonder tijdscomponent. Om te voorkomen dat de kalenderlogica per ongeluk
// afhankelijk wordt van de tijdzone van de machine die de code uitvoert
// (server of bezoekersbrowser, die allebei een andere lokale tijdzone kunnen
// hebben), bouwen we Date-objecten altijd met de LOKALE constructor
// (new Date(jaar, maand, dag)) en lezen we uitsluitend lokale getters/
// date-fns-functies terug - nooit gemengd met UTC-varianten. Zo blijft
// "12 september" altijd "12 september", ongeacht in welke tijdzone de code
// toevallig draait. Alleen waar we echt "vandaag" in Europe/Amsterdam nodig
// hebben (voorbereidingstijd/boekingsperiode) gebruiken we expliciet Intl
// met een IANA-tijdzone.

const DATE_FORMAT_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const TIME_FORMAT_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

function parseDateParts(dateString: string): [number, number, number] {
  const [year, month, day] = dateString.split("-").map(Number);
  return [year!, month!, day!];
}

function toLocalDate(dateString: string): Date {
  const [year, month, day] = parseDateParts(dateString);
  return new Date(year, month - 1, day);
}

export function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function isValidDateString(value: string): boolean {
  if (!DATE_FORMAT_REGEX.test(value)) return false;
  const [year, month, day] = parseDateParts(value);
  const date = toLocalDate(value);
  return (
    isValid(date) &&
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

export function isValidTimeString(value: string): boolean {
  return TIME_FORMAT_REGEX.test(value);
}

export function timeToMinutes(value: string): number {
  const [hours, minutes] = value.split(":").map(Number);
  return hours! * 60 + minutes!;
}

/** Duur van het evenement in minuten, rekening houdend met "eindtijd is na middernacht". */
export function eventDurationMinutes(
  startTime: string,
  endTime: string,
  endNextDay: boolean,
): number {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (endNextDay) {
    return 24 * 60 - start + end;
  }
  return end - start;
}

/** Vandaag als YYYY-MM-DD in de opgegeven IANA-tijdzone (standaard Europe/Amsterdam). */
export function todayInTimeZone(timeZone: string = "Europe/Amsterdam"): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(new Date());
}

/** Aantal kalenderdagen tussen twee YYYY-MM-DD-strings (b - a). */
export function daysBetween(aDateString: string, bDateString: string): number {
  return differenceInCalendarDays(toLocalDate(bDateString), toLocalDate(aDateString));
}

export function addDaysToDateString(dateString: string, days: number): string {
  return toDateString(addDays(toLocalDate(dateString), days));
}

export function addMonthsToDateString(dateString: string, months: number): string {
  return toDateString(addMonths(toLocalDate(dateString), months));
}

/** Genereert alle YYYY-MM-DD-strings tussen from en to (inclusief), oplopend. */
export function enumerateDateRange(fromDate: string, toDate: string): string[] {
  const dates: string[] = [];
  let cursor = fromDate;
  let guard = 0;
  while (cursor <= toDate && guard < 3700) {
    dates.push(cursor);
    cursor = addDaysToDateString(cursor, 1);
    guard += 1;
  }
  return dates;
}

/** Formatteert YYYY-MM-DD als bijvoorbeeld "zaterdag 12 september 2026". */
export function formatDateLong(dateString: string): string {
  return format(toLocalDate(dateString), "EEEE d MMMM yyyy", { locale: nl });
}

/** Dag van de week voor een YYYY-MM-DD-string, 0 = maandag ... 6 = zondag. */
export function weekdayMondayFirst(dateString: string): number {
  return (toLocalDate(dateString).getDay() + 6) % 7;
}

export { DATE_FORMAT_REGEX, TIME_FORMAT_REGEX };
